import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  Button,
  Alert,
  Form,
  Row,
  Col,
  Collapse,
  Badge,
} from "react-bootstrap";
import TradesTypeahead from "../TradesTypeahead/TradesTypeahead";
import axios from "../../services/axios";
import "./ProjectFeatureMapModalV2.scss";

const featureActions = ["Cosmetic", "Non-pressing", "Critical"];
const DEFAULT_FEATURE_REF_ID = 618;

const ProjectFeatureMapModalV2 = ({
  show,
  onHide,
  map,
  mode,
  onSave,
  onDelete,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    uuid: "",
    unitOfMeassure: "",
    trade_id: null,
    feature_action_ref_id: null,
    feature_ref_id: DEFAULT_FEATURE_REF_ID,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const submittingRef = useRef(false);

  // Update form data when map changes
  useEffect(() => {
    if (map) {
      setFormData({
        name: map.name || "",
        description: map.description || "",
        uuid: map.uuid || "",
        unitOfMeassure: map.unitOfMeassure || "",
        trade_id: map.trade_id || null,
        feature_action_ref_id: map.feature_action_ref_id ?? null,
        feature_ref_id: map.feature_ref_id ?? DEFAULT_FEATURE_REF_ID,
      });
    } else {
      setFormData({
        name: "",
        description: "",
        uuid: "",
        unitOfMeassure: "",
        trade_id: null,
        feature_action_ref_id: null,
        feature_ref_id: DEFAULT_FEATURE_REF_ID,
      });
      setShowAdvanced(false);
    }
    setError(null);
    setValidationErrors({});
    setShowDeleteConfirm(false);
  }, [map, show]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors((prev) => ({
        ...prev,
        [field]: null,
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name || formData.name.trim() === "") {
      errors.name = "Name is required";
    }

    if (!formData.trade_id) {
      errors.trade_id = "Trade is required";
    }

    if (!formData.uuid || formData.uuid.trim() === "") {
      errors.uuid = "UUID is required";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
      e.stopPropagation();
    }

    // Prevent double submission
    if (submittingRef.current || loading) {
      return;
    }

    // Validate form
    if (!validateForm()) {
      setError("Please fill in all required fields");
      return;
    }

    submittingRef.current = true;
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const dataToSave = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        uuid: formData.uuid.trim(),
        trade_id: formData.trade_id,
        unitOfMeassure: formData.unitOfMeassure.trim() || null,
        feature_action_ref_id: formData.feature_action_ref_id || null,
        // Some environments still require this field at the DB layer.
        feature_ref_id: formData.feature_ref_id || DEFAULT_FEATURE_REF_ID,
      };

      let response;
      if (mode === "edit" && map?.id) {
        response = await axios.put(
          `/domoreport/uploader/projectFeatureMap/${map.id}`,
          dataToSave,
        );
      } else {
        response = await axios.post(
          "/domoreport/uploader/projectFeatureMap/",
          dataToSave,
        );
      }

      setSuccess(
        mode === "edit"
          ? "Project feature map updated successfully"
          : "Project feature map created successfully",
      );

      // Notify parent and close after a short delay
      setTimeout(() => {
        if (onSave) {
          onSave(response?.data);
        }
        handleClose();
      }, 800);
    } catch (error) {
      console.error("Error saving PFM:", error);
      const apiMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.response?.data;

      if (typeof apiMessage === "string") {
        setError(apiMessage);
      } else if (error.response?.status === 409) {
        setError("A project feature map with this UUID already exists");
      } else {
        setError("Failed to save project feature map. Please try again.");
      }
    } finally {
      setLoading(false);
      submittingRef.current = false;
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    setError(null);

    try {
      await axios.delete(`/domoreport/uploader/projectFeatureMap/${map.id}`);
      setSuccess("Project feature map deleted successfully");

      setTimeout(() => {
        if (onDelete) {
          onDelete(map.id);
        }
        handleClose();
      }, 800);
    } catch (error) {
      console.error("Error deleting PFM:", error);
      setError(
        error?.response?.data?.error ||
          error?.message ||
          "Failed to delete project feature map",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError(null);
      setSuccess(null);
      setValidationErrors({});
      setShowDeleteConfirm(false);
      onHide();
    }
  };

  const isFormValid =
    formData.name &&
    formData.name.trim() !== "" &&
    formData.trade_id &&
    formData.uuid &&
    formData.uuid.trim() !== "";

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      centered
      backdrop="static"
      keyboard={!loading}
      className="pfm-modal-v2"
    >
      <Modal.Header closeButton={!loading}>
        <Modal.Title>
          <i className={`fa fa-${mode === "edit" ? "pencil" : "plus"} mr-2`} />
          {mode === "edit"
            ? "Edit Project Feature Map"
            : "Create New Project Feature Map"}
          {mode === "edit" && map && (
            <Badge bg="primary" className="ml-2">
              ID: {map.id}
            </Badge>
          )}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && (
          <Alert
            variant="danger"
            className="mb-3"
            dismissible
            onClose={() => setError(null)}
          >
            <i className="fa fa-exclamation-circle mr-2" />
            {error}
          </Alert>
        )}
        {success && (
          <Alert variant="success" className="mb-3">
            <i className="fa fa-check-circle mr-2" />
            {success}
          </Alert>
        )}

        {showDeleteConfirm ? (
          <div className="text-center py-4">
            <div className="mb-4">
              <i
                className="fa fa-exclamation-triangle text-danger"
                style={{ fontSize: "3rem" }}
              />
            </div>
            <h5 className="text-danger mb-3">Confirm Deletion</h5>
            <p className="mb-4">
              Are you sure you want to delete this project feature map?
              <br />
              <strong>This action cannot be undone.</strong>
            </p>
            {map && (
              <div className="bg-light p-3 rounded mb-4">
                <table className="table table-sm table-borderless mb-0">
                  <tbody>
                    <tr>
                      <td className="text-right font-weight-bold">ID:</td>
                      <td>{map.id}</td>
                    </tr>
                    <tr>
                      <td className="text-right font-weight-bold">
                        Project Name:
                      </td>
                      <td>{map.name}</td>
                    </tr>
                    <tr>
                      <td className="text-right font-weight-bold">UUID:</td>
                      <td>{map.uuid}</td>
                    </tr>
                    {map.trade && (
                      <tr>
                        <td className="text-right font-weight-bold">Trade:</td>
                        <td>
                          {map.trade.trade_icon && (
                            <i
                              className={`fa fa-${map.trade.trade_icon} mr-2`}
                            />
                          )}
                          {map.trade.trade_name}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : (
          <Form onSubmit={handleSubmit} noValidate>
            {/* Primary Fields */}
            <div className="primary-fields">
              <h6 className="section-title">
                <i className="fa fa-asterisk text-danger mr-2" />
                Required Information
              </h6>

              <Form.Group className="mb-3">
                <Form.Label>
                  Project Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  placeholder="Enter project name"
                  required
                  disabled={loading}
                  isInvalid={!!validationErrors.name}
                />
                <Form.Control.Feedback type="invalid">
                  {validationErrors.name}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>
                  Trade <span className="text-danger">*</span>
                </Form.Label>
                <TradesTypeahead
                  project={formData}
                  setProject={setFormData}
                  errors={validationErrors}
                />
                {validationErrors.trade_id && (
                  <div className="invalid-feedback d-block">
                    {validationErrors.trade_id}
                  </div>
                )}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={formData.description}
                  onChange={(e) =>
                    handleInputChange("description", e.target.value)
                  }
                  placeholder="Enter description (optional)"
                  disabled={loading}
                />
              </Form.Group>
            </div>

            {/* Advanced Fields (Collapsible) */}
            <div className="advanced-fields mt-4">
              <Button
                variant="link"
                className="p-0 mb-0 text-decoration-none"
                onClick={() => setShowAdvanced(!showAdvanced)}
                aria-expanded={showAdvanced}
              >
                <i
                  className={`fa fa-chevron-${showAdvanced ? "down" : "right"} mr-2`}
                />
                Advanced Fields
              </Button>

              <Collapse in={showAdvanced}>
                <div>
                  <Row className="mt-2 mb-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>
                          UUID <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          value={formData.uuid}
                          onChange={(e) =>
                            handleInputChange("uuid", e.target.value)
                          }
                          placeholder="Enter UUID"
                          required
                          disabled={loading}
                          isInvalid={!!validationErrors.uuid}
                        />
                        <Form.Control.Feedback type="invalid">
                          {validationErrors.uuid}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Unit of Measure</Form.Label>
                        <Form.Control
                          type="text"
                          value={formData.unitOfMeassure}
                          onChange={(e) =>
                            handleInputChange("unitOfMeassure", e.target.value)
                          }
                          placeholder="Enter unit (optional)"
                          disabled={loading}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Priority Level</Form.Label>
                        <Form.Control
                          as="select"
                          value={formData.feature_action_ref_id ?? ""}
                          onChange={(e) =>
                            handleInputChange(
                              "feature_action_ref_id",
                              e.target.value === ""
                                ? null
                                : parseInt(e.target.value, 10),
                            )
                          }
                          disabled={loading}
                        >
                          <option value="">— None —</option>
                          {featureActions.map((label, idx) => (
                            <option key={idx} value={idx + 1}>
                              {label}
                            </option>
                          ))}
                        </Form.Control>
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              </Collapse>
            </div>
          </Form>
        )}
      </Modal.Body>

      <Modal.Footer>
        {showDeleteConfirm ? (
          <div className="d-flex w-100 justify-content-end">
            <Button
              variant="outline-secondary"
              onClick={() => setShowDeleteConfirm(false)}
              disabled={loading}
              className="mr-2"
            >
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDelete} disabled={loading}>
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm mr-2" />
                  Deleting...
                </>
              ) : (
                <>
                  <i className="fa fa-trash mr-2" />
                  Confirm Delete
                </>
              )}
            </Button>
          </div>
        ) : (
          <div className="d-flex w-100 justify-content-between">
            <div>
              {mode === "edit" && map && (
                <Button
                  variant="outline-danger"
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={loading}
                >
                  <i className="fa fa-trash mr-2" />
                  Delete
                </Button>
              )}
            </div>
            <div className="d-flex">
              <Button
                variant="outline-secondary"
                onClick={handleClose}
                disabled={loading}
                className="mr-2"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                type="button"
                disabled={loading || !isFormValid}
                onClick={handleSubmit}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm mr-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <i
                      className={`fa fa-${mode === "edit" ? "save" : "plus"} mr-2`}
                    />
                    {mode === "edit" ? "Update" : "Create"}
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default ProjectFeatureMapModalV2;
